<template>
  <div
    class="customizer d-none d-md-block"
    :class="{'open': isCustomizerOpen}"
  >
    <!-- Toggler -->
    <b-link
      class="customizer-toggle d-flex align-items-center justify-content-center"
      @click="toggleCustomizer()"
    >
      <feather-icon
        icon="SettingsIcon"
        size="15"
        class="spinner"
      />
    </b-link>
    <!-- /Toggler -->

    <!-- Header -->
    <div class="customizer-section d-flex justify-content-between align-items-center">
      <div>
        <h4 class="mb-0">
          System Settings
        </h4>
        <small>Set system controls for sensitive operations</small>
      </div>
      <feather-icon
        icon="XIcon"
        size="18"
        class="cursor-pointer"
        @click="toggleCustomizer()"
      />
    </div>
    <!-- Header -->

    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="ps-customizer-area scroll-area"
    >
      <!-- Skin, RTL, Router Animation -->
      <validation-observer ref="dataForm" #default="{ invalid }">
        <b-form ref="form" @submit.stop.prevent="">
          <div class="customizer-section pt-0" v-for="(configuration, i) in configurations">
            <h6 class="mb-0">{{ configuration.name }}</h6>
            <small v-if="configuration.description !== null" style="font-style: italic;">{{ configuration.description }}</small>
            <b-row v-if="configuration.preferences.length > 0">
              <b-col :cols="Math.floor(10 / configuration.preferences.length)" v-for="(preference, x) in configuration.preferences">
                <b-form-group :label-for="preference.key.toLowerCase()" :label="preference.key">
                  <validation-provider #default="{ errors }" :name="preference.key" rules="">
                      <b-form-input
                          :id="preference.key.toLowerCase()"
                          :name="preference.key.toLowerCase()"
                          v-model="configurations[i].preferences[x].value"
                          :state="errors.length > 0 ? false : null"
                          size="sm"
                          number
                          :disabled="!preference.updating"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                      <small class="text-danger" v-if="serverErrors && serverErrors[preference.key.toLowerCase()]">{{ serverErrors[preference.key.toLowerCase()][0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col cols="2">
                <div class="pt-1">
                  <b-button
                    v-ripple.400="'rgba(40, 199, 111, 0.15)'"
                    variant="flat-secondary"
                    class="btn-icon"
                  >
                    <feather-icon icon="Edit3Icon" />
                  </b-button>
                </div>
              </b-col>
            </b-row>

            <div class="pl-1" v-if="configuration.components.length > 0">
              <b-row v-for="(component, m) in configuration.components">
                <b-col cols="12">
                  <h6>{{ component.name }}</h6>
                  <b-row v-if="component.preferences.length > 0">
                    <b-col :cols="Math.floor(10 / component.preferences.length)" v-for="(preference, n) in component.preferences">
                      <b-form-group :label-for="preference.key.toLowerCase()" :label="preference.key">
                        <validation-provider #default="{ errors }" :name="preference.key" rules="">
                            <b-form-input
                              :id="preference.key.toLowerCase()"
                              :name="preference.key.toLowerCase()"
                              v-model="configurations[i].components[m].preferences[n].value"
                              :state="errors.length > 0 ? false : null"
                              size="sm"
                              number
                              :disabled="!preference.updating"
                              @change="checkSaving(i, m)"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                            <small class="text-danger" v-if="serverErrors && serverErrors[preference.key.toLowerCase()]">{{ serverErrors[preference.key.toLowerCase()][0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <b-col cols="2">
                      <div class="pt-1">
                        <b-button
                          v-ripple.400="'rgba(40, 199, 111, 0.15)'"
                          :variant="component.preferences.filter(pref => pref.updating).length > 0 ? 'flat-success' : 'flat-secondary'"
                          class="btn-icon"
                          @click="toggleUpdating(i, m)"
                        >
                          <feather-icon v-if="configurations[i].components[m].preferences.filter(pref => !pref.updating).length > 0" icon="Edit3Icon" />
                          <feather-icon v-if="configurations[i].components[m].preferences.filter(pref => pref.updating).length > 0 && configurations[i].components[m].preferences.filter(pref => pref.needSaving).length === 0" icon="PlayIcon" />
                          <b-spinner small v-if="configurations[i].components[m].preferences.filter(pref => pref.saving).length > 0" />
                        </b-button>
                      </div>
                    </b-col>
                  </b-row>
                  <!-- </b-row> -->
                </b-col>
              </b-row>
            </div>
          </div>
        </b-form>
      </validation-observer>
    </vue-perfect-scrollbar>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import {
  BRow, BCol,
  BForm, BFormGroup, BFormInput, BFormCheckbox,
  BLink, BFormRadioGroup, BButton,
  BSpinner,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import useAppCustomizer from './useAppCustomizer'
import customizersStoreModule from './customizerStoreModule'
import { ref, onUnmounted } from '@vue/composition-api'
import store from '@/store'
import { get } from 'lodash'

export default {
  props: {},
  components: {
    // BSV
    BRow, BCol,
    BForm, BFormInput,
    BLink,
    BFormRadioGroup,
    BFormCheckbox,
    BFormGroup,
    BButton, BSpinner,
    ValidationObserver, ValidationProvider,

    // 3rd party
    vSelect,
    VuePerfectScrollbar,

  },
  directives: {
    Ripple,
  },
  setup(props, context) {
    const loading = ref(false)
    const saving = ref(false)
    const dataForm = ref(null)
    const serverErrors = ref(null)
    const settings = ref([])
    const configurations = ref([])

    const CROMIS_STORE_MODULE_NAME = 'cromis-customizer'
    // Register module
    if (!store.hasModule(CROMIS_STORE_MODULE_NAME)) store.registerModule(CROMIS_STORE_MODULE_NAME, customizersStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
        if (store.hasModule(CROMIS_STORE_MODULE_NAME)) store.unregisterModule(CROMIS_STORE_MODULE_NAME)
    })

    const {
      // Vertical Menu
      isVerticalMenuCollapsed,

      // Customizer
      isCustomizerOpen,

      // Skin
      skin,
      skinOptions,

      // Content Width
      contentWidth,
      contentWidthOptions,

      // RTL
      isRTL,

      // routerTransition
      routerTransition,
      routerTransitionOptions,

      // Layout Type
      layoutType,
      layoutTypeOptions,

      // NavMenu Hidden
      isNavMenuHidden,

      // Navbar
      navbarColors,
      navbarTypes,
      navbarBackgroundColor,
      navbarType,

      // Footer
      footerTypes,
      footerType,
    } = useAppCustomizer()

    if (layoutType.value === 'horizontal') {
      // Remove semi-dark skin option in horizontal layout
      const skinSemiDarkIndex = skinOptions.findIndex(s => s.value === 'semi-dark')
      delete skinOptions[skinSemiDarkIndex]

      // Remove menu hidden radio in horizontal layout => As we already have switch for it
      const menuHiddneIndex = navbarTypes.findIndex(t => t.value === 'hidden')
      delete navbarTypes[menuHiddneIndex]
    }

    // Perfect Scrollbar
    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const toggleCustomizer = async () => {
      isCustomizerOpen.value = !isCustomizerOpen.value

      if(isCustomizerOpen.value) {
        loading.value = true
        await fetchSettings()
        loading.value = false
      }
    }

    const toggleUpdating = async(i, m) => {
      if(m < 0 && configurations.value[i].preferences.filter(pref => pref.needSaving).length === 0){
        configurations.value[i].preferences.forEach((pref, x) => {
          configurations.value[i].preferences[x].updating = !configurations.value[i].preferences[x].updating
        })

        return
      }

      if(m >= 0 && configurations.value[i].components.length > 0 && configurations.value[i].components[m].preferences.filter(pref => pref.needSaving).length === 0){
        configurations.value[i].components[m].preferences.forEach((pref, n) => {
          configurations.value[i].components[m].preferences[n].updating = !configurations.value[i].components[m].preferences[n].updating
        })

        return
      }

      let preferences = []
      configurations.value[i].preferences.forEach((pref, x) => {
        if(pref.previsousValue !== pref.value){
          preferences.push(pref)
          configurations.value[i].preferences[x].saving = true
        }
      })

      if(m >= 0 && configurations.value[i].components.length > 0)
        configurations.value[i].components[m].preferences.forEach((pref, n) => {
          if(pref.previsousValue !== pref.value){
            preferences.push(pref)
            configurations.value[i].components[m].preferences[n].saving = true
          }
        })

      if(preferences.length > 0) {
        await store.dispatch(`${CROMIS_STORE_MODULE_NAME}/updatePreferences`, {preferences})
                  .then(response => {
                    fetchSettings()
                  })
                  .catch(error => {
                    console.log(error)
                    preferences.forEach(pref => {
                      pref.saving = false
                    })
                  })
      }
    }

    const checkSaving = (i, m) => {
      configurations.value[i].components[m].preferences.forEach((pref, n) => {
        if(pref.value !== pref.previsousValue) {
          configurations.value[i].components[m].preferences[n].needSaving = true
        }
        else {
          configurations.value[i].components[m].preferences[n].needSaving = false
        }
      })
    }

    const fetchSettings = async () => {
      await store.dispatch(`${CROMIS_STORE_MODULE_NAME}/list`)
                  .then(response => {
                    settings.value = response.data.settings

                    configurations.value = settings.value.map(setting => {
                      return {
                        id: setting.id,
                        name: setting.name,
                        description: setting.description,
                        preferences: setting.preferences.map(preference => {
                          return {
                            id: preference.id,
                            preference_id: setting.id,
                            preference_type: 0,
                            key: preference.key,
                            previsousValue: preference.value,
                            value: preference.value,
                            updating: false,
                            needSaving: false,
                            saving: false,
                          }
                        }),
                        components: setting.components.map(component => {
                          return {
                            id: component.id,
                            name: component.name,
                            preferences: component.preferences.map(preference => {
                              return {
                                id: preference.id,
                                preference_id: component.id,
                                preference_type: 1,
                                key: preference.key,
                                previsousValue: preference.value,
                                value: preference.value,
                                updating: false,
                                needSaving: false,
                                saving: false,
                              }
                            })
                          }
                        })
                      }
                    })
                  })
                  .catch(error => {
                    loading.value = false
                    console.log(error)
                  })
    }

    return {
      // Data
      loading,
      saving,
      dataForm,
      serverErrors,
      settings,
      configurations,

      // Vertical Menu
      isVerticalMenuCollapsed,

      // Customizer
      isCustomizerOpen,

      // Skin
      skin,
      skinOptions,

      // Content Width
      contentWidth,
      contentWidthOptions,

      // RTL
      isRTL,

      // routerTransition
      routerTransition,
      routerTransitionOptions,

      // Layout Type
      layoutType,
      layoutTypeOptions,

      // NavMenu Hidden
      isNavMenuHidden,

      // Navbar
      navbarColors,
      navbarTypes,
      navbarBackgroundColor,
      navbarType,

      // Footer
      footerTypes,
      footerType,

      // Perfect Scrollbar
      perfectScrollbarSettings,
      toggleCustomizer,
      toggleUpdating,
      checkSaving,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';

.customizer-section {
  padding: 1.5rem;
    border-bottom: 1px solid #ebe9f1;

  .dark-layout & {
    border-color: $theme-dark-border-color;
  }

  #skin-radio-group ::v-deep {
    .custom-control-inline {
      margin-right: 0.7rem;
    }
  }

  .form-group {
    margin-bottom: 1.5rem;;
    &:last-of-type {
    margin-bottom: 0;
    }
    ::v-deep legend {
      font-weight: 500;
    }
  }
}

.mark-active {
  box-shadow: 0 0 0 0.2rem rgba(38,143,255,.5);
}

.ps-customizer-area {
  height: calc(100% - 83px)
}
</style>
